import React from 'react'
import {
  useController,
  useFormState,
  type FieldPath,
  type FieldValues,
  type UseFormReturn,
} from 'react-hook-form'

import { Checkbox, CheckboxField as CatalystCheckboxField } from '../../catalyst/checkbox'
import { Description, ErrorMessage, Field, Label } from '../../catalyst/fieldset'
import { Input, InputGroup } from '../../catalyst/input'
import { Select } from '../../catalyst/select'
import { Textarea } from '../../catalyst/textarea'

import { formatPhoneNumber } from './phone'

// Field wrappers that bind react-hook-form to the Catalyst controls.
//
// Headless UI's `Field` already generates the ids and wires `aria-describedby`
// from the control to whichever `Description`/`ErrorMessage` sit inside it, and
// the `invalid` prop sets both `data-invalid` (for the red border) and
// `aria-invalid`. So the accessible wiring comes free as long as every field
// goes through here — which is the point. Hand-rolled ids drift; these cannot.
//
// Text, textarea and select stay *uncontrolled*, registered by ref, for two
// reasons. It is react-hook-form's faster path, and more importantly the DOM
// stays the source of truth — so Capybara's `fill_in` reaches form state the
// same way a person's typing does. Controlled inputs need `set_react_input`
// instead, so only the fields that genuinely need to rewrite the user's
// keystrokes (the phone mask) or hold non-string state (the checkbox) are
// controlled.

interface BaseFieldProps<T extends FieldValues> {
  form: UseFormReturn<T>
  /** Form state key. Dotted for nested params: `user.email`. */
  name: FieldPath<T>
  label: React.ReactNode
  /** Persistent help text. Stays visible when an error appears; it isn't a placeholder. */
  description?: React.ReactNode
  /**
   * `name` attribute posted to Rails, in the `user[email]` style the
   * controllers expect. Defaults to `name`.
   *
   * Safe to differ from `name`: react-hook-form resolves the DOM name through
   * the same path parser, and `user[email]` and `user.email` parse to the very
   * same location. The two spellings address one field.
   *
   * With one exception, and it bites silently. A *subscription* —
   * `watch('user.email')` or `useWatch({ name })` — matches the announced name
   * as a plain string, not through the parser. The change is announced under
   * the DOM name, so a filtered subscription on the dotted path never fires
   * and whatever it drives never updates. When a form needs to react to a
   * value, subscribe unfiltered (`useWatch({ control })`) and read the field
   * off the result. `events/RsvpForm.tsx` does this and says why.
   */
  submitName?: string
  /** Most fields are required, so label the exceptions rather than starring the rule. */
  optional?: boolean
  disabled?: boolean
  className?: string
}

function FieldLabel({ label, optional }: { label: React.ReactNode; optional?: boolean }) {
  return (
    <Label>
      {label}
      {optional && (
        <span className="ml-1 font-normal text-[color:var(--theme-muted)]">(optional)</span>
      )}
    </Label>
  )
}

/** Subscribes to one field's error without re-rendering the form for the others. */
function useFieldError<T extends FieldValues>(form: UseFormReturn<T>, name: FieldPath<T>) {
  const { errors } = useFormState({ control: form.control, name })
  // `errors` is nested to match the field path, so walk it the same way.
  const error = name
    .split('.')
    .reduce<unknown>((node, key) => (node as Record<string, unknown>)?.[key], errors)
  return (error as { message?: string } | undefined)?.message
}

type TextFieldProps<T extends FieldValues> = BaseFieldProps<T> & {
  type?: 'text' | 'email' | 'password' | 'url' | 'search' | 'number' | 'date' | 'datetime-local'
  placeholder?: string
  autoComplete?: string
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  autoFocus?: boolean
  maxLength?: number
  min?: string | number
  max?: string | number
  step?: string | number
  /**
   * Shown and submitted, but not editable — for a value the form still has to
   * post while the user is not allowed to change it. Unlike `disabled`, which
   * drops the field from the submission entirely.
   */
  readOnly?: boolean
  /**
   * Rewrites each keystroke before it reaches form state, for fields that
   * should refuse a character outright rather than accept then scold.
   */
  transform?: (value: string) => string
  /**
   * Unit marker drawn inside the input's leading edge — `$` on a money field.
   *
   * It is decoration, not content: the label still names the field, and the
   * value posted is the bare number. Kept here rather than left to each caller
   * so a currency input looks the same wherever one appears.
   */
  prefix?: React.ReactNode
}

export function TextField<T extends FieldValues>({
  form,
  name,
  label,
  description,
  submitName,
  optional,
  disabled,
  className,
  type = 'text',
  transform,
  prefix,
  ...inputProps
}: TextFieldProps<T>) {
  const error = useFieldError(form, name)
  const { onChange, onBlur, ref } = form.register(name)

  const input = (
    <Input
      {...inputProps}
      type={type}
      name={submitName ?? name}
      ref={ref}
      onChange={(event) => {
        if (transform) {
          // The input owns its value, so rewrite it in place and let
          // react-hook-form read the corrected version back off the DOM.
          event.target.value = transform(event.target.value)
        }
        return onChange(event)
      }}
      onBlur={onBlur}
      disabled={disabled}
      invalid={!!error}
    />
  )

  return (
    <Field className={className} disabled={disabled}>
      <FieldLabel label={label} optional={optional} />
      {description && <Description>{description}</Description>}
      {prefix ? (
        <InputGroup>
          {/* `data-slot=icon` is what InputGroup keys its leading padding off,
              so the marker sits in the input's gutter rather than over a digit. */}
          <div
            data-slot="icon"
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 flex items-center justify-center pl-2.5 text-[color:var(--theme-muted)]"
          >
            <span className="sm:text-sm">{prefix}</span>
          </div>
          {input}
        </InputGroup>
      ) : (
        input
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Field>
  )
}

/**
 * Phone input that masks as the user types.
 *
 * The mask is the affordance: it shows the expected shape without a
 * placeholder that vanishes on focus, and it chunks the digits so a
 * transposed one is easier to spot. State holds the formatted string, and the
 * schema validates the digits underneath it.
 *
 * Controlled, because masking means replacing what the user typed.
 */
export function PhoneField<T extends FieldValues>({
  form,
  name,
  label,
  description,
  submitName,
  optional,
  disabled,
  className,
  placeholder = '(555) 555-5555',
  autoComplete = 'tel',
}: BaseFieldProps<T> & { placeholder?: string; autoComplete?: string }) {
  const { field } = useController({ control: form.control, name })
  const error = useFieldError(form, name)

  return (
    <Field className={className} disabled={disabled}>
      <FieldLabel label={label} optional={optional} />
      {description && <Description>{description}</Description>}
      <Input
        type="tel"
        inputMode="tel"
        autoComplete={autoComplete}
        placeholder={placeholder}
        name={submitName ?? name}
        ref={field.ref}
        value={formatPhoneNumber(field.value)}
        onChange={(event) => field.onChange(formatPhoneNumber(event.target.value))}
        onBlur={field.onBlur}
        disabled={disabled}
        invalid={!!error}
      />
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Field>
  )
}

export function TextareaField<T extends FieldValues>({
  form,
  name,
  label,
  description,
  submitName,
  optional,
  disabled,
  className,
  rows,
  placeholder,
}: BaseFieldProps<T> & { rows?: number; placeholder?: string }) {
  const error = useFieldError(form, name)
  const { onChange, onBlur, ref } = form.register(name)

  return (
    <Field className={className} disabled={disabled}>
      <FieldLabel label={label} optional={optional} />
      {description && <Description>{description}</Description>}
      <Textarea
        rows={rows}
        placeholder={placeholder}
        name={submitName ?? name}
        ref={ref}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        invalid={!!error}
      />
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Field>
  )
}

export function SelectField<T extends FieldValues>({
  form,
  name,
  label,
  description,
  submitName,
  optional,
  disabled,
  className,
  children,
}: BaseFieldProps<T> & { children: React.ReactNode }) {
  const error = useFieldError(form, name)
  const { onChange, onBlur, ref } = form.register(name)

  return (
    <Field className={className} disabled={disabled}>
      <FieldLabel label={label} optional={optional} />
      {description && <Description>{description}</Description>}
      <Select
        name={submitName ?? name}
        ref={ref}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        invalid={!!error}
      >
        {children}
      </Select>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Field>
  )
}

/**
 * A single checkbox that carries its own rule, such as terms acceptance.
 *
 * The error has to sit *inside* `CheckboxField`: it renders a Headless
 * `Description` under the hood, which throws outright if it can't find a
 * `Field` above it. `CheckboxField` lays its children out on a two-column
 * grid and only positions `data-slot=description`, so the error — which
 * carries `data-slot=error` — is placed under the label explicitly.
 */
export function ConsentField<T extends FieldValues>({
  form,
  name,
  label,
  submitName,
  disabled,
  className,
  value = '1',
}: Omit<BaseFieldProps<T>, 'description' | 'optional'> & { value?: string }) {
  const { field } = useController({ control: form.control, name })
  const error = useFieldError(form, name)

  return (
    <div className={className}>
      <CatalystCheckboxField disabled={disabled}>
        {/* Headless's Checkbox has no `invalid` prop the way Input does, so the
            invalid state is announced here rather than inferred from a border. */}
        <Checkbox
          name={submitName ?? name}
          value={value}
          checked={!!field.value}
          onChange={field.onChange}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
        />
        <Label>{label}</Label>
        {error && (
          <ErrorMessage className="col-start-2 row-start-2" role="alert">
            {error}
          </ErrorMessage>
        )}
      </CatalystCheckboxField>
    </div>
  )
}

/**
 * Form-level message for what no single field owns — a failed request, or a
 * server error that belongs to no attribute on this form.
 */
export function FormBanner({ children }: { children: React.ReactNode }) {
  if (!children) return null

  return (
    <div
      role="alert"
      className="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/50 dark:text-red-200"
    >
      {children}
    </div>
  )
}
